/**
 * Tiers and what is in them — MTC-FCS-017.
 * ============================================================================
 * Three tiers, built out of two stacked LEVELS. Free is the base; Level I on
 * top of it is Premium; Level II on top of that is Lead Generation. The level
 * names ("Professional", "Lead Capture") are how the pricing was described to
 * us and are deliberately NOT shown anywhere — a customer sees Free, Premium,
 * Lead Generation and a list of what they get.
 *
 * ── ONE LIST, READ BY EVERYTHING ───────────────────────────────────────────
 * The benefit checkboxes, the tier tabs, the checkout summary and the price
 * all come from here. Pricing that is written down twice is pricing that
 * eventually disagrees with itself in front of a customer.
 *
 * ── WHICH BENEFITS ACTUALLY DO SOMETHING ───────────────────────────────────
 * `feature` marks an item that changes the generated website, and those are
 * the ones the preview, the export and the publish route honour. Items with no
 * `feature` are commercial — a domain credit, design credits — and are kept by
 * a person, not by this code. Both belong on a pricing list; only the first
 * kind may claim to be switched on, which is why the distinction is in the
 * data rather than in somebody's head.
 *
 * ── THE MONEY COMES FROM project.json — MTC-FCS-030 ───────────────────────
 * This file owns the WORDS: which benefits exist, which tier they belong to,
 * which of them actually switch something on. The AMOUNTS come from
 * project.json, which is also what `npm run setup:stripe` creates the Stripe
 * prices from — so the number on the pricing table is the number on the card
 * statement, by construction rather than by somebody remembering to change
 * both.
 *
 * Every price is still a PLACEHOLDER until signed off. Set the
 * STRIPE_PRICE_ID_* variables and Stripe owns them instead.
 */

import { PAID_PLANS, CURRENCY, INTERVAL, planByKey } from "../config/project"

/**
 * What one level ADDS, worked out from the cumulative tier prices.
 *
 * project.json stores what each tier costs in total, because that is the number
 * Stripe charges and the only one that has to be right. The pricing table talks
 * in differences ("+$4.99"), so the difference is derived here — the direction
 * that cannot drift. A missing plan gives 0 rather than NaN: a level with no
 * price should read as included, never as "$NaN/month".
 */
function levelAmount(key) {
  const index = PAID_PLANS.findIndex(p => p.key === key)
  if (index === -1) return 0
  const previous = index === 0 ? 0 : PAID_PLANS[index - 1].amount || 0
  return Math.max(0, (planByKey(key)?.amount || 0) - previous)
}

export const LEVELS = [
  {
    id: "professional",
    tier: "premium",
    /** What this level adds to the one below it — see levelAmount(). */
    amount: levelAmount("premium"),
    items: [
      {
        id: "hosting",
        label: "Hosting on your own address, no contracts",
        /** The item that turns the level on. Checking it checks the rest. */
        anchor: true,
      },
      {
        id: "domain",
        label: "A custom domain — standard domain credit, first year free, or bring your own",
      },
      { id: "design", label: "Premium page designs", feature: "templates" },
      { id: "branding", label: "No FreeContractorServices footer", feature: "branding" },
      // No `feature`: there is no pop-up in the product yet, so this is a
      // commitment about what Free will carry, not a switch that does
      // anything today. Marking it as a feature would make the tick a lie.
      { id: "nopopup", label: "No occasional pop-up on your site" },
      { id: "gallery", label: "A gallery of your own work", feature: "gallery" },
      { id: "invoices", label: "Invoices saved to your account" },
      { id: "qr", label: "QR code tracking" },
    ],
  },
  {
    id: "lead-capture",
    tier: "leads",
    // +$4.99 — MTC-FCS-029. Worked out from project.json rather than typed:
    // 2498 - 1999. The number actually charged comes from Stripe when
    // STRIPE_PRICE_ID_LEADS is set.
    amount: levelAmount("leads"),
    items: [
      // Lead capture is both the anchor and the feature: the level is named
      // after it, so a separate switch above an identical one would be two
      // controls for one idea.
      {
        id: "leadform",
        label: "Lead capture — a quote form on your website",
        anchor: true,
        feature: "leadform",
      },
      { id: "seo", label: "Premium SEO for your images" },
      {
        id: "credits",
        label: "$200 in custom design credits",
        // The most valuable line on the list and the least self-evident,
        // so it is the first one to carry an explanation — MTC-FCS-026.
        help:
          "Schedule a live call with one of our senior developers and have something custom built for your page.",
      },
      { id: "login", label: "Login for your customers" },
    ],
  },
]

/** Free first, then one tier per level, each priced cumulatively. */
export const TIERS = (() => {
  const out = {
    free: {
      id: "free",
      name: "Free",
      tagline: "Build it, see it, download it.",
      blurb: "Your website, yours to keep.",
      amount: 0,
      currency: CURRENCY,
      interval: INTERVAL,
      priceEnv: null,
      levels: [],
    },
  }
  let running = 0
  const names = Object.fromEntries(PAID_PLANS.map(p => [p.key, p.label]))
  const taglines = {
    premium: "Online on your own address, and your own branding only.",
    leads: "Everything in Premium, plus the tools that bring work in.",
  }
  const priceEnvs = Object.fromEntries(PAID_PLANS.map(p => [p.key, p.envVar]))

  for (const level of LEVELS) {
    running += level.amount
    out[level.tier] = {
      id: level.tier,
      name: names[level.tier],
      // The tagline is copy and lives here; project.json's one-line summary is
      // the fallback so a plan added there is never shown with a blank line.
      tagline: taglines[level.tier] || planByKey(level.tier)?.summary || "",
      blurb: taglines[level.tier] || planByKey(level.tier)?.summary || "",
      amount: running,
      currency: CURRENCY,
      interval: INTERVAL,
      priceEnv: priceEnvs[level.tier],
      // Cumulative: Lead Generation includes Premium's level as well.
      levels: LEVELS.slice(0, LEVELS.indexOf(level) + 1).map(l => l.id),
    }
  }
  return out
})()

/** Free first, then project.json's plans in the order it lists them. */
export const TIER_ORDER = ["free", ...PAID_PLANS.map(p => p.key)]

/** Anything unrecognised is Free. A bad id must never become a free upgrade. */
export function tierById(id) {
  return TIERS[id] || TIERS.free
}

/** The levels a tier includes, in order. */
export function levelsForTier(tierId) {
  const tier = tierById(tierId)
  return LEVELS.filter(level => tier.levels.includes(level.id))
}

/** The tier a level belongs to. */
export function tierForLevel(levelId) {
  const level = LEVELS.find(l => l.id === levelId)
  return level ? level.tier : "free"
}

/** Every item that changes the website, keyed by item id. */
export const FEATURE_ITEMS = LEVELS.flatMap(level =>
  level.items.filter(item => item.feature).map(item => ({ ...item, level: level.id }))
)

/** The extra a level costs on top of the one below it. */
export function levelById(id) {
  return LEVELS.find(level => level.id === id) || null
}
